/// Per-level session: holds the aircraft list, advances the simulation,
/// and tracks the level-end state.
///
/// Mirrors the air-only slice of FlatoutATC/Models/GameViewModel.swift
/// (aircraft list + tick + goal recognition). Three goal types are
/// fully wired: `land`, `transition`, `takeoff`. SID/STAR (departure
/// /arrival) and ground-control goals are deferred.
///
/// Spawn cadence: aircraft come from three sources:
///   1. Preset entries with explicit initialPosition/heading/alt/speed
///      (tutorial RJ123) — placed immediately on construction.
///   2. `initialSpawn` config blocks — random aircraft placed
///      immediately, one batch per level start.
///   3. Preset entries WITHOUT explicit position — released over time
///      at the active difficulty's rateOfAircraftPerHour. The session
///      walks the script once and stops.
///
/// Level success is computed when there are no more pending script
/// entries AND every active aircraft has either `landed` or
/// `completed`. Success = handled-successfully count ≥
/// minimumSuccessPercentage × handled-total. Tutorial (single
/// aircraft) treats any success as a level pass.

import { Aircraft } from './Aircraft'
import type { LevelBundle } from '../data/loadLevel'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import {
  HOLD_ENTER_SCORE,
  tickApproachIntercept,
  tickClimbout,
  tickDirectTo,
  tickFixExit,
  tickSurvey,
  tickFlightPlan,
  tickHelipadLanding,
  tickHold,
  tickLanding,
  type ExitInfo,
  type FlightPlanCompletionEvent,
} from './Autopilot'
import type {
  AircraftGoal,
  AircraftType,
  DifficultySettings,
  InitialSpawnConfig,
  LevelData,
  LevelGoalsSummary,
  PossibleGoal,
  PresetAircraftData,
  Runway,
} from '../types/LevelData'
import {
  pickActiveRunwayEnd,
  pickRunwayForTakeoffGoal,
  spawnByGoal,
} from './spawner'
import {
  DEFAULT_TAKEOFF_QUEUE_DEADLINE_SEC,
  queueKeyFor,
  type PendingAircraft,
} from './PendingAircraft'
import { defaultRng, type RandomSource } from './rng'
import { performanceFor } from './AircraftDatabase'
import { TrafficFeed, type LiveSpawn } from './liveTraffic/feed'
import { bearing, angDiff } from './liveTraffic/classifier'
import { resolveShiftAirports, workedAirportOf, shouldForceAI, isShiftComplete } from './liveShift'
import { updateAIAircraft, type AIContext } from './AIController'
import { rollEmergency } from './emergencies'
import {
  detectAircraftCollisions,
  detectTerrainCollisions,
  detectWeatherCollisions,
} from './CollisionDetector'
import { estimatePoolBreakdown, pickFromPool } from './goalPool'
import {
  crossingFixText,
  emergencyDeclareText,
  establishedText,
  levelOffText,
  spawnCheckInText,
} from './checkIns'
import type { Thunderstorm } from './Thunderstorm'
import { WeatherEngine } from './WeatherEngine'
import { ComboTracker, type ComboEvent } from './comboBonuses'
import {
  applyAirportMotion,
  captureInitialAirportState,
  type MovingAirportState,
} from './airportMotion'

export type LevelEndState =
  /// Live positioned shift only: the pre-shift position/goal picker is up and
  /// the feed is held until the player starts the shift.
  | { kind: 'preShift' }
  | { kind: 'inProgress' }
  | { kind: 'finished'; success: boolean }

/// Category an awarded ScoreEvent rolls up under for the
/// end-of-level breakdown. Distinct from the +/- sign of the
/// points: a wrong-airport landing is still a `landings` event.
export type ScoreCategory =
  | 'landings'
  | 'transitions'
  | 'departures'
  | 'conflicts'
  | 'bonuses'
  | 'violations'

export interface ScoreCategoryTotal {
  /// Number of awardScore calls in this category (regardless of sign).
  count: number
  /// Sum of post-multiplier points awarded to this category. Signed —
  /// crashes drag this negative even if the same category had
  /// successes earlier in the level.
  points: number
}

export type ScoreBreakdown = Record<ScoreCategory, ScoreCategoryTotal>

const SCORE_CATEGORIES: readonly ScoreCategory[] = [
  'landings',
  'transitions',
  'departures',
  'conflicts',
  'bonuses',
  'violations',
] as const

function emptyScoreBreakdown(): ScoreBreakdown {
  const out = {} as ScoreBreakdown
  for (const k of SCORE_CATEGORIES) out[k] = { count: 0, points: 0 }
  return out
}

/// A scoring event surfaced to the UI as it happens. Score values
/// match the iOS reference implementation (see GameViewModel.swift
/// updateScore calls): landing +100 / wrong-airport -150 / transition
/// +50 / departure +50. Multiplied by difficulty.scoreMultiplier.
export interface ScoreEvent {
  id: number
  description: string
  points: number
  /// Per-category bucket the event rolls up under for the end-of-
  /// level breakdown.
  category: ScoreCategory
  /// `performance.now()` value at award time. The UI fades events
  /// based on `now() - timestamp`.
  timestamp: number
}

export interface SessionListener {
  onTick?(dtSeconds: number): void
  onSelectionChange?(id: string | null): void
  onLevelEnd?(state: LevelEndState): void
  onScoreEvent?(event: ScoreEvent): void
  /// Spontaneous (non-command) ATC chatter emitted by the sim:
  /// spawn check-ins, fix crossings, level-offs, established on
  /// localizer, emergency declarations. The chrome forwards each
  /// to the readback toast stack.
  onReadback?(text: string): void
}

const IOS_PX_PER_NM = 100

/// Real-world reserved transponder codes — never issue these as a
/// per-aircraft squawk. 7700 is reserved for emergencies (the
/// session stamps emergency aircraft to 7700 at roll-time).
const RESERVED_SQUAWKS = new Set(['7500', '7600', '7700', '7000', '1200'])

/// Wind sim constants — Phase 1 of the wind feature: live
/// direction drift, fixed speed, no aerodynamic effects. Speed
/// goes live in a future phase.
const WIND_SPEED_KTS_PHASE1 = 10
/// Random-walk drift per sim-second (max). Each tick, we pick a
/// noise term in [-this, +this] degrees/sec.
const WIND_DRIFT_DEG_PER_SEC = 0.3
/// Pull-back-toward-preferred coefficient (per sim-second). Keeps
/// the random walk bounded but slow enough that jump events
/// persist for several minutes — otherwise no individual jump
/// would survive long enough to cross the 45° runway-re-eval
/// threshold. Tuned for a halflife around 230 sim-seconds.
const WIND_BIAS_PER_SEC = 0.003
/// Sim-seconds between scheduled "wind events" — sharper ±15–50°
/// step changes that simulate frontal passages. Phase 1 is purely
/// directional; speed sims come later. Magnitudes are
/// deliberately aggressive so a single jump can cross the
/// runway-re-eval threshold from the previously-evaluated state,
/// matching the player-perceptible "wind shifted, runway changed"
/// moment we're modelling.
const WIND_JUMP_MIN_INTERVAL_SEC = 180
const WIND_JUMP_MAX_INTERVAL_SEC = 480
const WIND_JUMP_MIN_DEG = 15
const WIND_JUMP_MAX_DEG = 50
/// When the live wind direction has shifted by this many degrees
/// since the last active-runway evaluation, re-evaluate (and
/// rebuild the localizer-arrow layer). 45° is the smallest shift
/// that can change which runway end is "active" given a 90°
/// crosswind threshold.
const WIND_RUNWAY_REEVAL_THRESHOLD_DEG = 45

/// Time-on-radar bonus, linear decay. Player gets up to
/// MAX_TIME_BONUS for handling an aircraft in zero sim seconds,
/// scaling down to 0 at TIME_BONUS_ZERO_AT_SEC. Tuned so an
/// efficient run gets meaningful credit but it isn't trivially
/// achievable on every handle — typical landings take 5–10 sim
/// minutes from edge spawn to touchdown.
const MAX_TIME_BONUS = 50
const TIME_BONUS_ZERO_AT_SEC = 600

const DEFAULT_DIFFICULTY: DifficultySettings = {
  rateOfAircraftPerHour: 30,
  emergencyChance: 0.05,
  scoreMultiplier: 1,
  minimumSuccessPercentage: 0.6,
}

export interface GameSessionOptions {
  bundle: LevelBundle
  projector: CoordinateProjector
  pxPerNM: number
  worldPx: number
  rng?: RandomSource
  difficultyName?: string
  /// Override the rate-of-aircraft-per-hour from the difficulty
  /// (handy for testing — `?rate=120` makes spawns visible in 30s).
  rateOverride?: number
}

/// Handoff boundary radius as a fraction of the world half-width — kept in
/// sync with the drawn ring (`HandoffBoundaryLayer`).
const LIVE_BOUNDARY_FRACTION = 0.46
/// How close (NM) to the boundary a leaving aircraft must be before it can be
/// handed off. Keeps you from clearing traffic to Center from mid-sector.
const HANDOFF_WINDOW_NM = 12

/// Map an ADS-B aircraft to the web game's coarse type tier (visuals + perf).
function liveAircraftType(spawn: LiveSpawn): AircraftType {
  const cat = spawn.category ?? ''
  const typ = spawn.icaoType ?? ''
  if (cat === 'A7') return 'helicopter'
  if (cat === 'A5' || ['B748', 'B77W', 'B772', 'B789', 'B788', 'A388', 'A35K'].includes(typ)) {
    return 'heavy'
  }
  if (cat === 'A1' || cat === 'A2') return 'light'
  return 'regionalJet' // A3/A4 airliners + bizjets → medium tier
}

export class GameSession {
  readonly bundle: LevelBundle
  readonly aircrafts: Aircraft[] = []

  /// Takeoff queues keyed by `<airportName>-<runwayId>`. Each entry
  /// is a lightweight `PendingAircraft` representing a takeoff slot
  /// that has been picked from the spawn pool/script but isn't yet
  /// materialised as an `Aircraft` on the runway threshold. Mirrors
  /// iOS's `takeoffQueues` dict on GameViewModel. Populated via
  /// `enqueueTakeoff`, drained via `promoteNextAircraftInQueue` once
  /// the target runway clears of arrivals on short final and other
  /// ground traffic. The renderer reads these to draw queue dots
  /// behind the threshold.
  private readonly takeoffQueues: Record<string, PendingAircraft[]> = {}
  readonly projector: CoordinateProjector
  readonly pxPerNM: number
  readonly worldPx: number
  readonly rng: RandomSource
  readonly difficulty: DifficultySettings
  readonly difficultyName: 'Novice' | 'Trainee' | 'Licensed' | 'Master'
  /// Pre-computed level-wide goal mix (counts per goal type + the
  /// completion target for the active difficulty). Surfaced to the
  /// brief and HUD so the player sees what success looks like.
  readonly goals: LevelGoalsSummary
  /// Wind direction (degrees magnetic) drives the localizer-arrow
  /// layer and the active-runway picker. Levels without a
  /// `fixedWindDirection` get a per-session random initial roll
  /// (matching iOS GameViewModel.swift:1794-1802) AND a live
  /// drift simulation: small ±0.3°/sec random walk biased back
  /// toward the initial value, plus occasional ±10–25° jumps
  /// every 3–8 sim minutes. Levels WITH `fixedWindDirection`
  /// hold steady — opt-out preserves authoring intent.
  ///
  /// Exposed as a getter so code that reads `session
  /// .effectiveWindDirection` keeps working; the underlying value
  /// is mutated by `tickWind`.
  get effectiveWindDirection(): number {
    return this._windDirectionDeg
  }
  /// Wind speed in knots. Phase 1: constant 10 kt. Phase 2 will
  /// add a speed simulation (5–25 kt typical) and wire it into
  /// landing-roll + groundspeed math.
  get windSpeedKts(): number {
    return WIND_SPEED_KTS_PHASE1
  }

  private listeners: SessionListener[] = []
  /// Readbacks emitted before the chrome attached its listener (e.g.
  /// spawn check-ins for explicit-positioned aircraft created in
  /// the constructor). Drained on the first `addListener` call.
  private _pendingReadbacks: string[] = []
  private _selectedId: string | null = null
  private _endState: LevelEndState = { kind: 'inProgress' }
  private _expectedTotal = 0
  private _handledSuccess = 0
  /// Aircraft cleared to the next sector via the Hand off command (live levels).
  private _handoffs = 0
  /// Aircraft that left the boundary unworked — the live shift's penalty metric.
  private _unhandledExits = 0
  private _handledFail = 0

  /// --- Live positioned-shift state (see liveShift.ts) ---
  /// Airports the player chose to work this shift; null = no positioned shift
  /// (the level drops straight in, like the pre-shift-framing behaviour).
  private _selectedAirports: Set<string> | null = null
  /// Aircraft-worked goal snapshotted at startShift(); 0 = open-ended.
  private _shiftGoal = 0
  /// Aircraft the PLAYER worked (landings + handoffs + worked exits) — AI-
  /// handled scenery does NOT count toward the shift goal.
  private _aircraftWorked = 0

  /// Active spawn mode. Levels declare exactly one of:
  ///   - `script`: explicit list of aircraft (presetAircraft.script).
  ///     Bounded by the script's length.
  ///   - `pool`: count-bounded random draws from `possibleGoals`
  ///     (randomAircraft.count).
  ///   - `timed`: continuous draws from `possibleGoals` until the
  ///     duration timer (scaled by the time multiplier, just like
  ///     the rest of the sim) drains.
  ///   - `idle`: no automated spawning (initialSpawn-only or empty).
  ///
  /// All four converge on the same `checkLevelEnd` predicate:
  /// nothing left to spawn AND every active aircraft handled.
  private _mode: SpawnMode
  /// Live ADS-B feed — non-null only on Handoff levels (`level.liveTraffic`).
  private feed: TrafficFeed | null = null
  /// Live aircraft count over the region right now (HUD), updated per poll.
  liveAircraftTotal = 0
  private _spawnIntervalSec: number
  private _secondsUntilNextSpawn = 0
  /// Monotonic counter for pool-spawned aircraft IDs.
  private _spawnSequence = 0
  /// Counter mod 10 — collision detection runs once per 10 ticks
  /// (matches iOS GameScene's throttle).
  private _collisionFrameCounter = 0
  /// Distinct conflict-pair entry events. Increments once when an
  /// (A,B) pair first enters separation conflict, then waits until
  /// they separate before re-arming. Drives the EndScreen skill
  /// rating.
  private _separationInfringements = 0
  /// Pairs currently in conflict (key: sorted ids joined by '|').
  /// Compared against the next pass's pairs to detect new entries.
  private _activeConflictPairs = new Set<string>()
  /// Sim-seconds elapsed since session start. Used by the combo
  /// tracker to enforce its rolling window without grabbing
  /// `Date.now()` (which would break under the time multiplier).
  private _simTimeSec = 0
  /// Squawk codes already assigned this session — generator
  /// re-rolls on collision so each aircraft gets a unique code.
  /// Never trimmed; 4096 octal codes vs sessions of dozens of
  /// aircraft means we never run out.
  private _usedSquawks = new Set<string>()
  /// Live wind direction (degrees magnetic). Mutates on tickWind
  /// when the level doesn't fix the wind. Public read via the
  /// `effectiveWindDirection` getter.
  private _windDirectionDeg: number
  /// Initial wind direction — drift biases back toward this value
  /// so the random walk doesn't accumulate without bound.
  private _windPreferredDirectionDeg: number
  /// True when the level published a `fixedWindDirection`. Wind
  /// holds steady in that case; tickWind is a no-op.
  private _windFixedByLevel: boolean
  /// Sim-seconds until the next sharp wind-event step.
  private _windNextJumpInSec: number
  /// Wind direction at the time of the last active-runway
  /// evaluation. Compare each tick to detect the 45° threshold
  /// for triggering a runway re-evaluation.
  private _windDirectionAtLastRunwayEval: number
  /// Set by `tickWind` when the wind direction has crossed the
  /// runway-re-evaluation threshold. The render side reads + clears
  /// this once per frame to rebuild the ApproachCourseLayer.
  private _windRunwayReEvalRequested = false
  /// Initial coords for moving airports (carriers). Per-tick motion
  /// translates `airport.centerCoordinate` + runway endpoints by
  /// the cumulative delta from these initial values, so all
  /// downstream geometry (touchdown, approach, render) reads the
  /// fresh "live" coordinates.
  private _movingAirportStates = new Map<string, MovingAirportState>()
  /// Combo / streak bonuses (triple arrival, departure rush,
  /// empty radar). Fed by the success-record paths and ticked for
  /// the empty-radar edge.
  private _comboTracker = new ComboTracker()
  /// Time-compression factor. Every wall-clock dt gets scaled by this
  /// before the simulation integrates, so flips affect heading turns,
  /// climb/descent, position, takeoff acceleration, spawn cadence,
  /// and emergency countdowns uniformly. 1× is real-time.
  private _timeMultiplier = 1
  private _totalScore = 0
  private _scoreEvents: ScoreEvent[] = []
  private _scoreEventCounter = 0
  /// Running per-category totals for the end-of-level breakdown.
  /// Updated on every `awardScore` call.
  private _breakdown: ScoreBreakdown = emptyScoreBreakdown()

  /// Weather engine. Owns the live thunderstorm list, ticks lifecycle,
  /// emits warning toasts. Always present, but on levels with
  /// `thunderstormChance: 0` (or absent) the spawn-roll never fires.
  readonly weather: WeatherEngine

  /// Per-level out-of-bounds extent. Computed at construction as the
  /// union of the base `worldPx` rect and every named entity (airport
  /// centers + waypoint coords), padded by `OOB_HANDOFF_BUFFER_NM`.
  /// Used by `pruneOutOfBoundsAircraft` instead of a fixed 0…worldPx
  /// check. Pre-fix, levels with waypoints geographically outside the
  /// nominal world radius (e.g. KTEX's DRO at 48 NM south of an 80-NM
  /// world's center → 8 NM outside worldBounds) would trip OOB before
  /// the aircraft reached the fix. Mirrors iOS commit `11ccb0b` on
  /// branch 2.4. 14 shipping levels were affected on iOS; the level
  /// JSONs are shared, so the same 14 levels are affected here.
  private readonly _oobMinX: number
  private readonly _oobMaxX: number
  private readonly _oobMinY: number
  private readonly _oobMaxY: number

  constructor(args: GameSessionOptions) {
    this.bundle = args.bundle
    this.projector = args.projector
    this.pxPerNM = args.pxPerNM
    this.worldPx = args.worldPx
    this.rng = args.rng ?? defaultRng

    // Pre-compute the OOB extent (see field doc for rationale). 5 NM
    // handoff buffer beyond the farthest named entity mirrors the iOS
    // safety net that auto-completes a transition when its fix has
    // been passed but the aircraft trips OOB before the handoff
    // radius. Web doesn't have that explicit safety net yet, but a
    // 5 NM pad gives enough room for the aircraft to actually reach
    // the fix and score before tripping OOB.
    const OOB_HANDOFF_BUFFER_NM = 5
    const bufferPx = OOB_HANDOFF_BUFFER_NM * this.pxPerNM
    let minX = 0
    let maxX = this.worldPx
    let minY = 0
    let maxY = this.worldPx
    for (const ap of this.bundle.level.airports) {
      const p = this.projector.project(ap.centerCoordinate, true)
      if (p.x - bufferPx < minX) minX = p.x - bufferPx
      if (p.x + bufferPx > maxX) maxX = p.x + bufferPx
      if (p.y - bufferPx < minY) minY = p.y - bufferPx
      if (p.y + bufferPx > maxY) maxY = p.y + bufferPx
    }
    for (const wp of this.bundle.level.waypoints) {
      const p = this.projector.project(wp.coordinate, true)
      if (p.x - bufferPx < minX) minX = p.x - bufferPx
      if (p.x + bufferPx > maxX) maxX = p.x + bufferPx
      if (p.y - bufferPx < minY) minY = p.y - bufferPx
      if (p.y + bufferPx > maxY) maxY = p.y + bufferPx
    }
    this._oobMinX = minX
    this._oobMaxX = maxX
    this._oobMinY = minY
    this._oobMaxY = maxY
    const resolved = resolveDifficultyAndName(args.bundle, args.difficultyName)
    this.difficulty = resolved.settings
    this.difficultyName = resolved.name

    // Roll the wind direction once per session. Drawing this from
    // the same RNG as spawning means the same seed produces the
    // same runway lineup — important for tests, but the RNG isn't
    // seeded in production so each playthrough still varies.
    const fixedWind = args.bundle.level.fixedWindDirection
    const initialWind =
      fixedWind != null ? fixedWind : Math.floor(this.rng() * 360)
    this._windDirectionDeg = initialWind
    this._windPreferredDirectionDeg = initialWind
    this._windFixedByLevel = fixedWind != null
    this._windDirectionAtLastRunwayEval = initialWind
    this._windNextJumpInSec = randInJumpRange(this.rng)

    const ratePerHour = args.rateOverride ?? this.difficulty.rateOfAircraftPerHour
    this._spawnIntervalSec = ratePerHour > 0 ? 3600 / ratePerHour : Infinity
    // First aircraft from the script comes one interval after start.
    this._secondsUntilNextSpawn = this._spawnIntervalSec

    // Walks the explicit-positioned head of the script (e.g., RJ123
    // sitting at its initial position in the tutorial). Returns the
    // index where the timer-driven spawn picks up.
    const nextScriptIdx = this.spawnPresetExplicit()
    this.spawnInitial()
    this._mode = resolveSpawnMode(this.bundle.level, nextScriptIdx, ratePerHour)

    // Handoff levels: pull real aircraft from the live ADS-B feed instead of
    // the scripted/pool spawner (the scenario carries no aircraft). The feed
    // polls a same-origin proxy and queues classified spawns; `tickSpawner`
    // drains them and `spawnLiveAircraft` injects them at their true positions.
    const lt = this.bundle.level.liveTraffic
    if (lt) {
      this.feed = new TrafficFeed(
        lt.endpoint,
        undefined,
        lt.pollSeconds ?? 7.5,
        (s) => {
          this.liveAircraftTotal = s.total
        },
        (flowTrue) => this.applyLiveActiveFlow(flowTrue),
      )
      // Positioned shift: open on the pre-shift picker and hold the feed until
      // startShift(). Live levels without `positions` keep the old behaviour —
      // drop straight in with the feed already polling.
      if (lt.positions && lt.positions.length > 0) {
        this._endState = { kind: 'preShift' }
      } else {
        this.feed.start()
      }
    }
    this._expectedTotal = this.aircrafts.length + remainingFromMode(this._mode, ratePerHour)
    this.goals = computeLevelGoals(this.bundle.level, this.difficulty, this._expectedTotal, this._mode)

    this.weather = new WeatherEngine({
      pxPerNM: this.pxPerNM,
      worldPx: this.worldPx,
      thunderstormChance: this.difficulty.thunderstormChance ?? 0,
      difficultyName: this.difficultyName,
      rng: this.rng,
    })

    // Capture initial coords for any moving airports — applyAirportMotion
    // translates the live coordinates by the cumulative delta from
    // these snapshots each tick. Static airports (no `motion` field)
    // are skipped here and bypassed in the per-tick loop.
    for (const ap of this.bundle.level.airports) {
      if (ap.motion) {
        this._movingAirportStates.set(ap.name, captureInitialAirportState(ap))
      }
    }
  }

  /// Per-tick motion pass for moving airports. Called early in the
  /// tick — before per-aircraft logic — so touchdown detection,
  /// approach course, and render layers all see the new live
  /// runway threshold positions for this frame.
  private _aiAccumSec = 0
  /// Step the AI controller ~once per second for every AI-controlled aircraft.
  /// The AI sets targets + issues clearances; the per-frame autopilot ticks
  /// below execute them. Runs before the per-aircraft loop so commands land
  /// this frame.
  private tickAI(scaledDt: number): void {
    this._aiAccumSec += scaledDt
    if (this._aiAccumSec < 1) return
    this._aiAccumSec = 0
    let ctx: AIContext | null = null
    for (const a of this.aircrafts) {
      if (!a.aiControlled) continue
      if (!ctx) {
        ctx = {
          level: this.bundle.level,
          airports: this.bundle.level.airports,
          projector: this.projector,
          pxPerNM: this.pxPerNM,
          windDirection: this.effectiveWindDirection,
        }
      }
      updateAIAircraft(a, ctx)
    }
  }

  private tickAirportMotion(scaledDt: number): void {
    if (this._movingAirportStates.size === 0) return
    for (const ap of this.bundle.level.airports) {
      if (!ap.motion) continue
      const state = this._movingAirportStates.get(ap.name)
      if (!state) continue
      applyAirportMotion(ap, state, scaledDt)
    }
    // After advancing the airport positions, snap any
    // awaiting-takeoff aircraft sitting on a moving deck to the
    // live threshold so they slide along with the carrier instead
    // of getting left behind in the open ocean.
    for (const ac of this.aircrafts) {
      if (ac.status !== 'awaitingTakeoff') continue
      const dr = ac.departureRunway
      if (!dr) continue
      const ap = this.bundle.level.airports.find((a) => a.name === dr.airportName)
      if (!ap || !ap.motion) continue
      const runway = ap.runways.find(
        (r) => r.id1 === dr.runwayId || r.id2 === dr.runwayId,
      )
      if (!runway) continue
      const thresholdGeo =
        dr.runwayId === runway.id1 ? runway.startCoordinate : runway.endCoordinate
      ac.position = this.projector.project(thresholdGeo, true)
    }
  }

  /// Live pixel-space translation for a moving airport. Used by
  /// the carrier render layer to nudge its container each frame
  /// so the hull / deck visuals track the live position. Returns
  /// `null` for static airports.
  airportTranslationPx(airportName: string): { x: number; y: number } | null {
    const state = this._movingAirportStates.get(airportName)
    if (!state) return null
    const ap = this.bundle.level.airports.find((a) => a.name === airportName)
    if (!ap) return null
    const initial = this.projector.project(state.initialCenter, true)
    const current = this.projector.project(ap.centerCoordinate, true)
    return { x: current.x - initial.x, y: current.y - initial.y }
  }

  /// Live thunderstorms — render layer reads this every frame to
  /// diff/redraw weather cells.
  get thunderstorms(): readonly Thunderstorm[] {
    return this.weather.storms
  }

  addListener(l: SessionListener): () => void {
    this.listeners.push(l)
    // Drain any check-in readbacks queued before the chrome attached.
    if (this._pendingReadbacks.length > 0 && l.onReadback) {
      for (const text of this._pendingReadbacks) l.onReadback(text)
      this._pendingReadbacks.length = 0
    }
    return () => {
      this.listeners = this.listeners.filter((x) => x !== l)
    }
  }

  private emitReadback(text: string): void {
    if (this.listeners.length === 0) {
      this._pendingReadbacks.push(text)
      return
    }
    for (const l of this.listeners) l.onReadback?.(text)
  }

  get selectedId(): string | null {
    return this._selectedId
  }

  get endState(): LevelEndState {
    return this._endState
  }

  get successCount(): number {
    return this._handledSuccess
  }

  get failCount(): number {
    return this._handledFail
  }

  get expectedTotal(): number {
    return this._expectedTotal
  }

  get totalScore(): number {
    return this._totalScore
  }

  /// Most recent score events (newest first). Capped at 16 so the
  /// list never grows unbounded.
  recentScoreEvents(limit = 16): ScoreEvent[] {
    return this._scoreEvents.slice(0, limit)
  }

  private awardScore(
    description: string,
    basePoints: number,
    category: ScoreCategory,
  ): void {
    const points = Math.round(basePoints * this.difficulty.scoreMultiplier)
    // Clamp the running total at zero so a string of crashes /
    // wrong-airport landings can't bury the score in the negatives.
    // The individual event still records its full signed value so
    // the toast list shows what happened.
    this._totalScore = Math.max(0, this._totalScore + points)
    this._breakdown[category].count += 1
    this._breakdown[category].points += points
    this._scoreEventCounter += 1
    const event: ScoreEvent = {
      id: this._scoreEventCounter,
      description,
      points,
      category,
      timestamp: performance.now(),
    }
    this._scoreEvents.unshift(event)
    if (this._scoreEvents.length > 32) this._scoreEvents.length = 32
    this.listeners.forEach((l) => l.onScoreEvent?.(event))
  }

  /// Per-category running totals for the end-of-level breakdown.
  /// Returns the live object — read-only by convention.
  get breakdown(): ScoreBreakdown {
    return this._breakdown
  }

  /// Distinct separation-conflict episodes — counts each unique
  /// (A,B) pair once per entry into conflict, not every frame they
  /// stay in conflict. Crashes are tracked separately and don't
  /// contribute. Drives the EndScreen skill rating.
  get separationInfringements(): number {
    return this._separationInfringements
  }

  select(id: string | null): void {
    if (this._selectedId === id) return
    if (this._selectedId) {
      const prev = this.aircrafts.find((a) => a.id === this._selectedId)
      if (prev) prev.selected = false
    }
    this._selectedId = id
    if (id) {
      const next = this.aircrafts.find((a) => a.id === id)
      if (next) next.selected = true
    }
    this.listeners.forEach((l) => l.onSelectionChange?.(id))
  }

  selectedAircraft(): Aircraft | null {
    if (!this._selectedId) return null
    return this.aircrafts.find((a) => a.id === this._selectedId) ?? null
  }

  get timeMultiplier(): number {
    return this._timeMultiplier
  }

  setTimeMultiplier(m: number): void {
    if (!Number.isFinite(m) || m <= 0) return
    this._timeMultiplier = m
  }

  /// Advance the sim by `dt` seconds. Called from the RAF loop with
  /// wall-clock dt; we multiply by the active time-compression factor
  /// before passing it to the autopilot/aircraft/spawner so 2×, 4×,
  /// 8× speed-ups affect every time-sensitive subsystem uniformly.
  tick(dt: number): void {
    // preShift ticks too — so the position-picker's onTick sync fires (the
    // store learns we're in preShift and renders PositionSelect) and the radar
    // stays interactive. Only a finished level halts the loop. During preShift
    // there are no aircraft and the feed isn't polling, so the sim work below
    // is a harmless no-op until startShift() flips us to inProgress.
    if (this._endState.kind === 'finished') return

    const scaledDt = dt * this._timeMultiplier
    this._simTimeSec += scaledDt

    // Motion pass FIRST — moving-airport runway endpoints update
    // before any per-aircraft tick that reads them (touchdown,
    // approach intercept, runway selection).
    this.tickAirportMotion(scaledDt)
    this.tickSpawner(scaledDt)
    this.tickWeather(scaledDt)
    this.tickWind(scaledDt)
    this.tickAI(scaledDt)

    for (const aircraft of this.aircrafts) {
      // Capture pre-tick state for level-off detection: the autopilot
      // tick may change targetAltitudeFt, and aircraft.tick may
      // converge altitudeFt to it this same frame. We snapshot the
      // "was at target?" boolean BEFORE any of that runs.
      const wasAtAltitudeTarget = aircraft.altitudeFt === aircraft.targetAltitudeFt
      // Pre-tick status for rotation detection. tickClimbout transitions
      // a takingOff aircraft to enRoute once it's past Vr and above
      // field elevation + 50 ft; that's our "runway is now clear"
      // signal to promote the next queued takeoff onto the threshold.
      const wasTakingOff = aircraft.status === 'takingOff'

      // Time-on-radar accumulator. Skipped while the aircraft is
      // holding short (`awaitingTakeoff`) and after it's been
      // resolved (`landed` / `completed`); ticks for everything in
      // between. Drives the fast-handle bonus on successful goals.
      if (
        aircraft.status === 'takingOff' ||
        aircraft.status === 'enRoute' ||
        aircraft.status === 'landing'
      ) {
        aircraft.radarTimeSec += scaledDt
      }

      // Status-aware autopilot. Each handler is a no-op when the
      // aircraft isn't in its target state.
      const touchdown =
        tickLanding(
          aircraft,
          this.bundle.level.airports,
          this.pxPerNM,
          this.projector,
        ) ??
        tickHelipadLanding(
          aircraft,
          this.bundle.level.airports,
          this.pxPerNM,
          this.projector,
        )
      tickClimbout(aircraft, this.bundle.level.airports)
      // Survey hold-engage / hold-timer / hold-expire. Runs before
      // tickFixExit so the survey state machine gets first crack —
      // tickFixExit only fires the 'survey' exit once tickSurvey has
      // already routed the aircraft to its exit waypoint.
      tickSurvey(
        aircraft,
        this.bundle.level,
        this.projector,
        this.pxPerNM,
        scaledDt,
      )
      const exit = tickFixExit(aircraft, this.pxPerNM)
      // Hold autopilot runs before directTo so directTo's "arrived"
      // logic doesn't see (and clear) the hold's homing target. It's
      // a no-op when holdingState is null.
      const holdEnter = tickHold(aircraft, this.pxPerNM, scaledDt)
      // Flight plan walker (SID + STAR). Owns navigation when
      // engaged; tickDirectTo bails when flightPlan != null.
      const planEvent = tickFlightPlan(
        aircraft,
        this.bundle.level,
        this.projector,
        this.pxPerNM,
      )
      const directCrossing = tickDirectTo(aircraft, this.pxPerNM)
      // Localizer-intercept autopilot — runs after directTo so the
      // approach handler wins when both are technically active (it
      // checks status itself; landing/landed/completed = no-op).
      const established = tickApproachIntercept(
        aircraft,
        this.pxPerNM,
        this.bundle.level.airports,
        this.projector,
      )

      aircraft.tick(scaledDt, this.pxPerNM)
      this.tickEmergency(aircraft, scaledDt)

      // Rotation handoff: an aircraft that just transitioned from
      // `takingOff` to `enRoute` has cleared the runway. Trigger queue
      // promotion for that runway so the next dot becomes the new
      // `awaitingTakeoff` aircraft on the threshold. The short-final
      // gate inside `promoteNextAircraftInQueue` still blocks if an
      // arrival has reached the radius during the takeoff roll.
      if (
        wasTakingOff &&
        aircraft.status === 'enRoute' &&
        aircraft.departureRunway
      ) {
        const key = queueKeyFor(
          aircraft.departureRunway.airportName,
          aircraft.departureRunway.runwayId,
        )
        this.promoteNextAircraftInQueue(key)
      }

      if (touchdown) {
        this.recordLanding(aircraft, touchdown.airportName)
      }
      if (exit) {
        this.recordExit(aircraft, exit)
      }
      if (holdEnter) {
        this.awardScore(
          `Entered hold ${aircraft.flightNumber} at ${holdEnter.fixName}`,
          HOLD_ENTER_SCORE,
          'bonuses',
        )
      }

      // Flight plan events: per-leg violation, then completion.
      // A leg can violate both restrictions in one crossing — score
      // each independently so the EndScreen reflects the actual
      // count of busts, and the player gets a separate readback per
      // bust so they know which restriction they missed.
      if (planEvent?.leg) {
        const leg = planEvent.leg
        if (leg.altitudeViolation) {
          this.awardScore(
            `Altitude bust on ${leg.procedureName} at ${leg.waypointName}`,
            -25,
            'violations',
          )
          this.emitReadback(
            `${aircraft.flightNumber}, altitude restriction at ${leg.waypointName}`,
          )
        }
        if (leg.speedViolation) {
          this.awardScore(
            `Speed bust on ${leg.procedureName} at ${leg.waypointName}`,
            -15,
            'violations',
          )
          this.emitReadback(
            `${aircraft.flightNumber}, speed restriction at ${leg.waypointName}`,
          )
        }
      }
      if (planEvent?.completed) {
        this.handleFlightPlanCompletion(aircraft, planEvent.completed)
      }

      // Spontaneous-action readbacks.
      if (directCrossing) {
        this.emitReadback(crossingFixText(aircraft, directCrossing.fixName))
      }
      if (established) {
        this.emitReadback(establishedText(aircraft, established.runwayId))
      }
      // Level-off: skip during landing/takeoff phases — those are
      // continuous descents/climbs, not assigned cruise altitudes.
      if (
        !wasAtAltitudeTarget &&
        aircraft.altitudeFt === aircraft.targetAltitudeFt &&
        aircraft.status === 'enRoute'
      ) {
        this.emitReadback(levelOffText(aircraft))
      }
    }

    this._collisionFrameCounter = (this._collisionFrameCounter + 1) % 10
    if (this._collisionFrameCounter === 0) {
      this.runCollisionPass()
    }

    this.pruneOutOfBoundsAircraft()
    this.checkEmptyRadarBonus()
    this.checkLevelEnd()
    this.listeners.forEach((l) => l.onTick?.(scaledDt))
  }

  /// World-bounds sweep. Mirrors iOS's `aircraftOutOfBounds`:
  /// aircraft that wander outside the world rect without scoring
  /// their goal collect a goal-typed penalty; aircraft already
  /// `completed` or `landed` are removed silently. STAR-arrival
  /// aircraft mid-procedure are spared because the procedure
  /// itself can carry them across the boundary and back.
  private pruneOutOfBoundsAircraft(): void {
    const survivors: Aircraft[] = []
    for (const aircraft of this.aircrafts) {
      // Touched-down aircraft retire regardless of position. The
      // threshold sits inside the world rect, so without this guard
      // a landed plane would freeze at the runway (zero speed) and
      // AircraftLayer would keep redrawing its node — visually it
      // would look like it was "stuck" instead of "removed from
      // play." Score + handled-count bookkeeping happen in
      // `recordLanding` before the status flip, so this is just
      // node + array cleanup.
      if (aircraft.status === 'landed') continue

      const { x, y } = aircraft.position
      // Pre-fix this checked `0 ≤ x,y ≤ worldPx` — the base canvas
      // rect. Levels with named entities outside that rect (e.g.
      // KTEX's DRO at 48 NM south in an 80-NM world → ~8 NM outside)
      // produced "Missed exit" scores for aircraft tracking to those
      // fixes. The extent is now computed per-level at construction
      // as the union of (base rect, all entities + 5 NM buffer); see
      // the `_oobMinX/...` field doc.
      const inside =
        x >= this._oobMinX &&
        x <= this._oobMaxX &&
        y >= this._oobMinY &&
        y <= this._oobMaxY
      if (inside) {
        survivors.push(aircraft)
        continue
      }
      if (aircraft.status === 'completed') {
        // Already scored — drop silently. (Touched-down aircraft
        // were already filtered out above regardless of position.)
        continue
      }
      // STAR aircraft mid-procedure may legitimately leave the rect.
      if (aircraft.goal.arrival && aircraft.flightPlan) {
        survivors.push(aircraft)
        continue
      }
      this.recordOutOfBounds(aircraft)
    }
    if (survivors.length !== this.aircrafts.length) {
      this.aircrafts.length = 0
      for (const a of survivors) this.aircrafts.push(a)
      // At least one aircraft was just removed (landed, completed, or
      // OOB). Any runway it was occupying or short-finalling onto may
      // now be promotable. The per-queue gates inside
      // `promoteNextAircraftInQueue` filter out runways that still
      // have an arrival on short final or a departure rolling, so
      // this is cheap to call broadly.
      this.tryPromoteAllQueues()
    }
  }

  private recordOutOfBounds(aircraft: Aircraft): void {
    if (aircraft.emergency) {
      this.awardScore(
        `Emergency failed: ${aircraft.flightNumber}`,
        -1000,
        'bonuses',
      )
      this._handledFail += 1
      return
    }
    // Live "Handoff" levels: an aircraft that leaves the boundary unworked is a
    // gentle unhandled-exit (the shift's metric), not a harsh missed-approach —
    // you're not expected to work every plane in the live picture.
    if (this.bundle.level.liveTraffic) {
      this.awardScore(`Left unhandled: ${aircraft.flightNumber}`, -25, 'transitions')
      this._handledFail += 1
      this._unhandledExits += 1
      return
    }
    if (aircraft.goal.land || aircraft.goal.arrival) {
      this.awardScore(
        `Missed approach ${aircraft.flightNumber}`,
        -200,
        'landings',
      )
      this._handledFail += 1
      return
    }
    if (aircraft.goal.transition) {
      const wp = aircraft.goal.transition.waypointName
      this.awardScore(
        `Missed exit ${wp}: ${aircraft.flightNumber}`,
        -50,
        'transitions',
      )
      this._handledFail += 1
      return
    }
    if (aircraft.goal.takeoff || aircraft.goal.departure) {
      this.awardScore(
        `Missed departure ${aircraft.flightNumber}`,
        -50,
        'departures',
      )
      this._handledFail += 1
      return
    }
  }

  /// Fire the empty-radar combo when active traffic transitions to
  /// zero AND the level is still running (more spawns to come or
  /// queued takeoffs awaiting clearance). Sustained-empty doesn't
  /// keep awarding — the tracker only fires on the false→true edge.
  private checkEmptyRadarBonus(): void {
    const active = this.aircrafts.filter(
      (a) =>
        a.status !== 'awaitingTakeoff' &&
        a.status !== 'landed' &&
        a.status !== 'completed',
    ).length
    const stillRunning =
      this._endState.kind === 'inProgress' && this._expectedTotal > this._handledSuccess + this._handledFail
    const event = this._comboTracker.observeRadar(active, stillRunning)
    if (event) this.applyComboEvent(event)
  }

  private applyComboEvent(event: ComboEvent): void {
    this.awardScore(event.description, event.points, 'bonuses')
  }

  /// Aircraft-to-aircraft + aircraft-to-terrain collision sweep.
  /// Resets each aircraft's collisionStatus to 'normal' first so old
  /// conflicts clear when separation increases.
  private runCollisionPass(): void {
    for (const a of this.aircrafts) {
      if (a.collisionStatus !== 'crashed') {
        a.collisionStatus = 'normal'
        a.collisionDetail = null
      }
    }

    const aircraftHits = detectAircraftCollisions(this.aircrafts, this.pxPerNM)
    const newPairs = new Set<string>()
    for (const hit of aircraftHits) {
      const a = this.aircrafts.find((x) => x.id === hit.aircraftId1)
      const b = this.aircrafts.find((x) => x.id === hit.aircraftId2)
      if (!a || !b) continue
      if (hit.status === 'crashed') {
        this.markCrashed(a, `collided with ${b.flightNumber}`)
        this.markCrashed(b, `collided with ${a.flightNumber}`)
      } else {
        if (a.collisionStatus !== 'crashed') {
          a.collisionStatus = 'conflict'
          a.collisionDetail = `Traffic — ${b.flightNumber}`
        }
        if (b.collisionStatus !== 'crashed') {
          b.collisionStatus = 'conflict'
          b.collisionDetail = `Traffic — ${a.flightNumber}`
        }
        // Pair-entry edge: count once when this pair first appears
        // in the conflict set. Sorting the ids makes the key
        // order-independent.
        const key = pairKey(a.id, b.id)
        newPairs.add(key)
        if (!this._activeConflictPairs.has(key)) {
          this._separationInfringements += 1
          this.awardScore(
            `Separation lost: ${a.flightNumber} / ${b.flightNumber}`,
            -25,
            'conflicts',
          )
        }
      }
    }
    // Replace the active set so pairs that have separated this pass
    // are eligible to re-trigger if they conflict again later.
    this._activeConflictPairs = newPairs

    const terrain = this.bundle.level.terrainObstacles ?? []
    if (terrain.length > 0) {
      const terrainHits = detectTerrainCollisions({
        aircrafts: this.aircrafts,
        terrain,
        airports: this.bundle.level.airports,
        projector: this.projector,
        pxPerNM: this.pxPerNM,
      })
      for (const hit of terrainHits) {
        const a = this.aircrafts.find((x) => x.id === hit.aircraftId)
        if (!a) continue
        if (hit.status === 'crashed') {
          this.markCrashed(a, `terrain @ ${hit.terrainElevationFt} ft`)
        } else if (a.collisionStatus !== 'crashed' && a.collisionStatus !== 'conflict') {
          // Conflict (traffic) outranks terrainWarning when both are
          // active — traffic resolves on a shorter timescale.
          a.collisionStatus = 'terrainWarning'
          a.collisionDetail = `Terrain — ${hit.terrainElevationFt.toLocaleString()} ft`
        }
      }
    }

    if (this.weather.storms.length > 0) {
      const weatherHits = detectWeatherCollisions({
        aircrafts: this.aircrafts,
        storms: this.weather.storms,
        pxPerNM: this.pxPerNM,
      })
      for (const hit of weatherHits) {
        const a = this.aircrafts.find((x) => x.id === hit.aircraftId)
        if (!a) continue
        this.markCrashed(a, 'thunderstorm')
      }
    }
  }

  private markCrashed(aircraft: Aircraft, reason: string): void {
    if (aircraft.status === 'completed' || aircraft.collisionStatus === 'crashed') return
    aircraft.status = 'completed'
    aircraft.collisionStatus = 'crashed'
    aircraft.collisionDetail = reason
    aircraft.emergency = null
    this._handledFail += 1
    this.awardScore(`Crashed: ${aircraft.flightNumber} (${reason})`, -500, 'conflicts')
  }

  /// Phase-1 wind sim. Drifts the live wind direction each tick
  /// (random walk + bias-back toward the initial value, plus
  /// scheduled ±10–25° step events every 3–8 sim minutes).
  /// Levels with `fixedWindDirection` opt out — the field stays
  /// at the configured value so authoring intent is preserved.
  ///
  /// When the cumulative shift since the last runway evaluation
  /// crosses the 45° threshold, set `_windRunwayReEvalRequested`
  /// so the render side rebuilds the localizer-arrow layer on
  /// the next frame. Aircraft already cleared for an approach
  /// keep their cleared runway — the wind shift only affects
  /// new clearances.
  private tickWind(dt: number): void {
    if (this._windFixedByLevel) return

    // Random-walk drift in [-WIND_DRIFT_DEG_PER_SEC, +same] × dt.
    const noise = (this.rng() * 2 - 1) * WIND_DRIFT_DEG_PER_SEC * dt
    // Bias-back: pull a fraction of the way toward preferred each
    // sim-second. Uses shortest-angle delta so wrap-around (e.g.,
    // preferred = 350°, current = 10°) doesn't push the wrong way.
    const offset = shortestAngleDelta(this._windDirectionDeg, this._windPreferredDirectionDeg)
    const biasPull = offset * WIND_BIAS_PER_SEC * dt
    this._windDirectionDeg = wrapDeg(this._windDirectionDeg + noise + biasPull)

    // Sharp event timer — when it expires, apply a step jump and
    // reset.
    this._windNextJumpInSec -= dt
    if (this._windNextJumpInSec <= 0) {
      const magnitude =
        WIND_JUMP_MIN_DEG + this.rng() * (WIND_JUMP_MAX_DEG - WIND_JUMP_MIN_DEG)
      const sign = this.rng() < 0.5 ? -1 : 1
      this._windDirectionDeg = wrapDeg(this._windDirectionDeg + sign * magnitude)
      this._windNextJumpInSec = randInJumpRange(this.rng)
    }

    // Threshold check for active-runway re-evaluation.
    const driftFromLastEval = Math.abs(
      shortestAngleDelta(this._windDirectionAtLastRunwayEval, this._windDirectionDeg),
    )
    if (driftFromLastEval >= WIND_RUNWAY_REEVAL_THRESHOLD_DEG) {
      this._windRunwayReEvalRequested = true
      this._windDirectionAtLastRunwayEval = this._windDirectionDeg
      this.repositionAwaitingTakeoffsForActiveRunway()
    }
  }

  /// When the wind shifts past the runway-re-evaluation threshold,
  /// any aircraft already queued for takeoff are sitting at the
  /// runway end that was active *before* the shift. Move each one
  /// to the new active threshold (and update its heading + the
  /// `departureRunway.runwayId` it'll surface in the data tag).
  /// Cleared, mid-roll, and airborne aircraft are not touched —
  /// only `awaitingTakeoff` is in scope here.
  private repositionAwaitingTakeoffsForActiveRunway(): void {
    for (const aircraft of this.aircrafts) {
      if (aircraft.status !== 'awaitingTakeoff') continue
      const dep = aircraft.departureRunway
      if (!dep) continue
      const airport = this.bundle.level.airports.find((a) => a.name === dep.airportName)
      if (!airport || airport.runways.length === 0) continue
      // Departure goal (SID) pins the runway by procedure — leave
      // those alone. Plain takeoff goals are wind-driven.
      if (aircraft.goal.departure) continue
      const pick = pickActiveRunwayEnd(airport, this.effectiveWindDirection)
      if (!pick) continue
      const newRunwayId =
        pick.headingDeg === pick.runway.heading1 ? pick.runway.id1 : pick.runway.id2
      if (newRunwayId === dep.runwayId) continue
      const thresholdGeo =
        pick.headingDeg === pick.runway.heading1
          ? pick.runway.startCoordinate
          : pick.runway.endCoordinate
      const px = this.projector.project(thresholdGeo, true)
      aircraft.position = px
      aircraft.headingDeg = pick.headingDeg
      aircraft.targetHeadingDeg = pick.headingDeg
      aircraft.departureRunway = { airportName: airport.name, runwayId: newRunwayId }
    }
  }

  /// Returns true (and clears the flag) if the wind has shifted
  /// past the runway-re-evaluation threshold since the last call.
  /// The render side polls this each frame to decide whether to
  /// rebuild the localizer-arrow layer.
  consumeWindRunwayReEvalRequest(): boolean {
    if (!this._windRunwayReEvalRequested) return false
    this._windRunwayReEvalRequested = false
    return true
  }

  /// Drive the weather engine and surface its events as readbacks.
  /// Storms accumulate in `weather.storms`; the Pixi WeatherLayer
  /// reads from that list each frame and diff/redraws.
  private tickWeather(dt: number): void {
    const events = this.weather.tick(dt)
    for (const ev of events) {
      if (ev.kind === 'warning') {
        this.emitReadback('⚠️ Thunderstorm forming')
      }
    }
  }

  private tickEmergency(aircraft: Aircraft, dt: number): void {
    const e = aircraft.emergency
    if (!e) return
    if (
      aircraft.status === 'landed' ||
      aircraft.status === 'completed' ||
      aircraft.status === 'awaitingTakeoff'
    ) {
      return
    }
    e.countdownSec -= dt
    if (e.countdownSec > 0) return
    // Timed out — aircraft is lost. Mark completed-with-fail and
    // award the iOS-equivalent penalty.
    aircraft.status = 'completed'
    aircraft.emergency = null
    this._handledFail += 1
    this.awardScore(`Lost: ${aircraft.flightNumber}`, -250, 'conflicts')
  }

  /// Mode-aware spawner. Branches on `_mode`:
  ///   - script: walks the next preset script entry.
  ///   - pool: draws a random goal from `possibleGoals`.
  ///   - timed: same as pool, but also drains the duration timer.
  ///   - idle: no-op.
  private tickSpawner(dt: number): void {
    // Tick deadlines on every queued takeoff and reap any that aged
    // out. Runs before the spawn branch so a timed-out queue slot
    // doesn't survive into the same tick that adds a new slot.
    this.tickQueuedDeadlines(dt)

    if (this.feed) {
      // Live feed drives spawns; inject a few per tick so a poll's batch
      // trickles in rather than popping a dozen aircraft on one frame.
      for (const s of this.feed.drain(3)) this.spawnLiveAircraft(s)
      return
    }

    if (noMoreSpawns(this._mode)) return
    if (this._mode.kind === 'timed') {
      this._mode.remainingSec -= dt
      if (this._mode.remainingSec <= 0) return
    }
    this._secondsUntilNextSpawn -= dt
    while (this._secondsUntilNextSpawn <= 0 && !noMoreSpawns(this._mode)) {
      this._secondsUntilNextSpawn += this._spawnIntervalSec
      // Counter updates are unconditional now that the takeoff queue
      // exists — enqueue always succeeds (the slot just sits as a
      // dot until the runway clears). Pre-queue, `spawnNext*`
      // returned false on short-final block and we gated the counter
      // updates so a deferred goal wouldn't consume a level slot.
      // With the queue that gate is no longer needed: the slot is
      // committed at enqueue and the promotion logic owns the
      // "wait for runway" semantics.
      switch (this._mode.kind) {
        case 'script':
          this.spawnNextScriptEntry()
          break
        case 'pool':
          this.spawnNextFromPool()
          this._mode.remaining -= 1
          break
        case 'timed':
          this.spawnNextFromPool()
          this._mode.spawned += 1
          break
      }
    }
  }

  /// Tick every queued `PendingAircraft.timeToFirstWaypointSec` down by
  /// `dt` sim-seconds. Slots that hit zero are scored as missed
  /// departure windows and removed from their queue. Runs once per
  /// sim-tick from `tickSpawner` before the spawn branch.
  private tickQueuedDeadlines(dt: number): void {
    for (const key of Object.keys(this.takeoffQueues)) {
      const queue = this.takeoffQueues[key]
      if (!queue || queue.length === 0) continue
      const survivors: PendingAircraft[] = []
      for (const p of queue) {
        const remaining = p.timeToFirstWaypointSec - dt
        if (remaining <= 0) {
          this.awardScore(
            `Missed departure window: ${p.flightNumber}`,
            -50,
            'departures',
          )
          this._handledFail += 1
          continue
        }
        p.timeToFirstWaypointSec = remaining
        survivors.push(p)
      }
      this.takeoffQueues[key] = survivors
    }
  }

  /// Walk the next script entry. For takeoff/SID goals the result is
  /// a queued slot (a dot near the runway); for arrival/transition/
  /// land/survey goals the aircraft materialises immediately. Either
  /// way the script index advances unconditionally — failed lookups
  /// (unknown airport, missing waypoint) lose a slot rather than spin
  /// forever.
  private spawnNextScriptEntry(): void {
    if (this._mode.kind !== 'script') return
    const i = this._mode.nextIdx
    const entry = this._mode.script[i]
    if (!entry) return
    this._mode.nextIdx += 1
    const id = `preset-${i}-${entry.flightNumber ?? entry.type}`
    const flightNumber =
      entry.flightNumber ?? this.nextCallsign(entry.type, entry.goal)
    this.addAircraftFromGoal({
      id,
      flightNumber,
      type: entry.type,
      goal: entry.goal,
    })
  }

  /// Pool-mode pick. Returns void because the spawn outcome no longer
  /// gates level counters — the takeoff queue absorbs short-final
  /// deferrals as queued slots rather than as failed picks. Other goal
  /// types either materialise immediately or fail (level data error).
  private spawnNextFromPool(): void {
    const pool = poolOf(this._mode)
    if (!pool) return
    const pick = pickFromPool(pool, this.rng)
    if (!pick) return
    const seq = this._spawnSequence++
    const id = `pool-${seq}-${pick.type}`
    const flightNumber = this.nextCallsign(pick.type, pick.goal)
    this.addAircraftFromGoal({
      id,
      flightNumber,
      type: pick.type,
      goal: pick.goal,
    })
  }

  /// Time-on-radar bonus tier. Linear decay from `MAX_TIME_BONUS`
  /// at 0 sim seconds to 0 at `TIME_BONUS_ZERO_AT_SEC`. The result
  /// is rounded; <=0 means "no bonus this handle." Lifted into a
  /// helper so the same curve applies across landings, transitions,
  /// and SID departures.
  /// Inject one classified live aircraft at its real radar position. Arrivals
  /// to a playable airport get a landing goal; everything else (departures,
  /// satellite-field traffic, pass-throughs) gets a transition to the boundary
  /// fix nearest its track — vector it out and hand it off.
  private spawnLiveAircraft(spawn: LiveSpawn): void {
    const lt = this.bundle.level.liveTraffic
    if (!lt) return
    if (this.aircrafts.some((a) => a.id === `live-${spawn.hex}`)) return

    const playable = new Set(lt.playableAirports)
    const playableArrival =
      spawn.phase === 'arrival' &&
      spawn.assignedAirportICAO != null &&
      playable.has(spawn.assignedAirportICAO)

    let goal: AircraftGoal
    if (playableArrival) {
      goal = { land: { airportName: spawn.assignedAirportICAO as string } }
    } else {
      const fix = this.nearestBoundaryFix(spawn)
      if (!fix) return
      goal = { transition: { waypointName: fix } }
    }

    const position = this.projector.project(
      { latitude: spawn.lat, longitude: spawn.lon },
      true,
    )
    // ADS-B `track` is TRUE; the game (and the magnetic-north-up projection)
    // flies MAGNETIC headings. Convert so live aircraft line up with the
    // runways: magBrg = trueBrg − magneticVariation (NYC magVar ≈ −13 ⟹ +13).
    const magVar = this.bundle.level.airports[0]?.magneticVariation ?? 0
    const magneticHeading = (((spawn.trackDeg - magVar) % 360) + 360) % 360
    // Positioned shift: an arrival to an airport the player isn't working flies
    // itself; pass-through traffic + the no-shift case use the normal AI roll.
    const forceAI = shouldForceAI(workedAirportOf(goal), this._selectedAirports)
    const aircraft = new Aircraft({
      id: `live-${spawn.hex}`,
      flightNumber: spawn.callsign,
      type: liveAircraftType(spawn),
      goal,
      position,
      headingDeg: magneticHeading,
      altitudeFt: Math.max(1000, Math.round(spawn.altitudeFt)),
      speedKts: Math.max(120, Math.round(spawn.groundSpeedKt)),
      status: 'enRoute',
      // A fraction of live traffic flies itself (the AI controller), per the
      // difficulty's aiControlledPercentage — they auto-land / auto-exit so the
      // player works only part of the picture.
      aiControlled: forceAI || this.rng() < (this.difficulty.aiControlledPercentage ?? 0),
      squawk: this.nextSquawk(),
    })
    this.aircrafts.push(aircraft)
  }

  /// Boundary fix whose bearing from the aircraft best matches its track —
  /// the one it's flying toward, so a transition there reads as "exit".
  private nearestBoundaryFix(spawn: LiveSpawn): string | null {
    let best: string | null = null
    let bestOff = 999
    for (const wp of this.bundle.level.waypoints) {
      const brg = bearing(spawn.lat, spawn.lon, wp.coordinate.latitude, wp.coordinate.longitude)
      const off = angDiff(spawn.trackDeg, brg)
      if (off < bestOff) {
        bestOff = off
        best = wp.name
      }
    }
    return best
  }

  /// Drive the active runway from the REAL landing flow (inferred from live
  /// descending traffic each poll) instead of the level's placeholder wind, so
  /// the game lands the same direction NY is actually using. `flowTrue` is the
  /// TRUE final-approach heading ≈ the landing runway's true heading; convert to
  /// the magnetic wind the runway picker wants (windMag = flowTrue − magVar) and
  /// request a runway re-eval. Wind stays "fixed" (no random drift) on live levels.
  private applyLiveActiveFlow(flowTrue: number): void {
    const magVar = this.bundle.level.airports[0]?.magneticVariation ?? 0
    const windMag = wrapDeg(flowTrue - magVar)
    if (Math.abs(shortestAngleDelta(windMag, this._windDirectionDeg)) < 2) return
    this._windDirectionDeg = windMag
    this._windPreferredDirectionDeg = windMag
    this._windRunwayReEvalRequested = true
    this._windDirectionAtLastRunwayEval = windMag
    this.repositionAwaitingTakeoffsForActiveRunway()
  }

  /// Begin a positioned live shift from the pre-shift picker: lock in the
  /// airports the player works + the aircraft-worked goal, then start the feed.
  /// No-op unless we're in the preShift state.
  startShift(positionId: string, goal: number): void {
    if (this._endState.kind !== 'preShift') return
    this._selectedAirports = resolveShiftAirports(
      this.bundle.level.liveTraffic?.positions,
      positionId,
    )
    this._shiftGoal = Math.max(0, Math.round(goal))
    this._endState = { kind: 'inProgress' }
    this.feed?.start()
  }

  /// Player-worked tally for the live shift goal (AI scenery excluded).
  get aircraftWorked(): number {
    return this._aircraftWorked
  }
  /// Aircraft-worked target snapshotted at startShift (0 = open-ended).
  get shiftGoal(): number {
    return this._shiftGoal
  }
  /// Pre-shift position options for the picker (empty ⟹ not a positioned shift).
  get shiftPositions() {
    return this.bundle.level.liveTraffic?.positions ?? []
  }
  /// Aircraft-worked goal presets offered in the picker.
  get shiftGoalPresets(): number[] {
    return this.bundle.level.liveTraffic?.shiftGoalPresets ?? [10, 20, 40]
  }

  /// Credit one PLAYER-worked aircraft toward the live shift goal. AI-handled
  /// traffic (the positions you're not working) is scenery and does not count.
  private creditShiftWork(aircraft: Aircraft): void {
    if (!aircraft.aiControlled) this._aircraftWorked += 1
  }

  /// Stop the live feed poll loop. Called from `RadarScene.destroy()`.
  dispose(): void {
    this.feed?.stop()
    this.feed = null
  }

  /// Hand an aircraft off to the next sector — the namesake verb of the Handoff
  /// levels. Works on ANY aircraft regardless of goal: clears it off the radar
  /// as worked, with a small bonus and no penalty. Land the traffic you want,
  /// hand the rest to Center. No-op (returns false) on non-live levels.
  handOffAircraft(id: string): boolean {
    const a = this.aircrafts.find((x) => x.id === id)
    if (!a || !this.canHandOff(a)) return false
    this.awardScore(`Handed off to Center: ${a.flightNumber}`, 25, 'transitions')
    this._handledSuccess += 1
    this._aircraftWorked += 1
    this._handoffs += 1
    this.aircrafts.splice(this.aircrafts.indexOf(a), 1)
    if (this._selectedId === id) this._selectedId = null
    return true
  }

  /// The handoff-boundary radius (NM) for live levels — same value the drawn
  /// ring (`HandoffBoundaryLayer`) uses.
  get liveBoundaryRadiusNM(): number {
    return this.bundle.level.worldSizeInNM * LIVE_BOUNDARY_FRACTION
  }

  /// Whether an aircraft may be handed off right now. Three gates: it's a live
  /// level, the aircraft is LEAVING (a transition / pass-through goal — not an
  /// arrival you're meant to land), AND it's within `HANDOFF_WINDOW_NM` of the
  /// boundary (you can't hand traffic to Center from the middle of your sector).
  canHandOff(a: Aircraft): boolean {
    if (!this.bundle.level.liveTraffic) return false
    if (!a.goal.transition) return false
    const c = this.worldPx / 2
    const distNM = Math.hypot(a.position.x - c, a.position.y - c) / this.pxPerNM
    return distNM >= this.liveBoundaryRadiusNM - HANDOFF_WINDOW_NM
  }

  get handoffCount(): number {
    return this._handoffs
  }
  get unhandledExitCount(): number {
    return this._unhandledExits
  }
  get isLive(): boolean {
    return this.bundle.level.liveTraffic != null
  }

  private timeOnRadarBonus(aircraft: Aircraft): number {
    const elapsed = aircraft.radarTimeSec
    const ratio = 1 - elapsed / TIME_BONUS_ZERO_AT_SEC
    return Math.max(0, Math.round(MAX_TIME_BONUS * ratio))
  }

  private maybeAwardTimeBonus(aircraft: Aircraft, label: string): void {
    const bonus = this.timeOnRadarBonus(aircraft)
    if (bonus <= 0) return
    this.awardScore(
      `Quick ${label} ${aircraft.flightNumber} (${Math.round(aircraft.radarTimeSec)} s)`,
      bonus,
      'bonuses',
    )
  }

  /// Called the frame a SID/STAR completes its last leg.
  ///   - departure (SID complete): aircraft exits airspace via the
  ///     SID's last waypoint, scored as a successful departure.
  ///   - arrival (STAR complete): emit a "cleared for approach"
  ///     readback. The landing itself still has to happen via
  ///     player commands; goal completion is recorded by
  ///     `recordLanding` when the aircraft touches down.
  private handleFlightPlanCompletion(
    aircraft: Aircraft,
    event: FlightPlanCompletionEvent,
  ): void {
    if (event.kind === 'departure') {
      aircraft.status = 'completed'
      this._handledSuccess += 1
      this.creditShiftWork(aircraft)
      this.awardScore(
        `Departure ${aircraft.flightNumber} via ${event.procedureName}`,
        50,
        'departures',
      )
      this.maybeAwardTimeBonus(aircraft, 'departure')
      const combo = this._comboTracker.recordDeparture(this._simTimeSec)
      if (combo) this.applyComboEvent(combo)
      this.emitReadback(
        `${aircraft.flightNumber}, ${event.lastWaypoint}, radar contact lost`,
      )
    } else {
      // Arrival: STAR complete. The aircraft is now near the IAF
      // and ready for approach. We don't auto-engage approach
      // intercept here — the player picks the moment via Cleared
      // for approach (or skips straight to Cleared to land).
      this.emitReadback(
        `${aircraft.flightNumber}, ${event.procedureName} complete, ready for approach`,
      )
    }
  }

  private recordLanding(aircraft: Aircraft, airportName: string): void {
    // Emergency landings collect a bonus regardless of airport (any
    // safe field beats losing the aircraft).
    if (aircraft.emergency) {
      aircraft.emergency = null
      this._handledSuccess += 1
      this.creditShiftWork(aircraft)
      this.awardScore(`Emergency landed ${aircraft.flightNumber}`, 1000, 'bonuses')
      return
    }
    // Arrival goal (STAR) lands at its airport just like a `land`
    // goal. The two are semantic siblings — `land` aircraft skip
    // the procedure, `arrival` aircraft fly the STAR first — but
    // both end at a runway. `landHelipad` is the helicopter sibling
    // — same airport-match logic, but the touchdown happens on a
    // pad rather than a runway slab.
    const goalAirport =
      aircraft.goal.land?.airportName ??
      aircraft.goal.arrival?.airportName ??
      aircraft.goal.landHelipad?.airportName
    if (goalAirport === airportName) {
      this._handledSuccess += 1
      this.creditShiftWork(aircraft)
      this.awardScore(`Landed ${aircraft.flightNumber}`, 100, 'landings')
      this.maybeAwardTimeBonus(aircraft, 'landing')
      const combo = this._comboTracker.recordLanding(this._simTimeSec)
      if (combo) this.applyComboEvent(combo)
    } else if (goalAirport) {
      this._handledFail += 1
      this.awardScore(`Wrong airport: ${aircraft.flightNumber}`, -150, 'landings')
    } else {
      this._handledFail += 1
      this.awardScore(`Landed off-goal: ${aircraft.flightNumber}`, -50, 'landings')
    }
  }

  private recordExit(aircraft: Aircraft, exit: ExitInfo): void {
    if (exit.reason === 'survey') {
      // Survey traffic = decoration. Counts toward the level's handled
      // denominator (so a level whose pool rolled a survey can still
      // hit 100%) but earns no score bonus — the reward for the
      // survey goal is "you didn't crash into it." Mirrors iOS
      // GameViewModel.swift:1747-1761.
      const goalExit = aircraft.goal.survey?.exitWaypointName
      if (goalExit === exit.waypointName) {
        this._handledSuccess += 1
        this.creditShiftWork(aircraft)
      } else {
        // Should not happen — tickFixExit only fires 'survey' when
        // directToName matches the survey's exitWaypointName. Defensive
        // tally so the denominator stays consistent.
        this._handledFail += 1
      }
      return
    }
    if (exit.reason === 'transition') {
      const goalWp = aircraft.goal.transition?.waypointName
      if (goalWp === exit.waypointName) {
        this._handledSuccess += 1
        this.creditShiftWork(aircraft)
        this.awardScore(`Transition ${aircraft.flightNumber}`, 50, 'transitions')
        this.maybeAwardTimeBonus(aircraft, 'transition')
      } else {
        this._handledFail += 1
        this.awardScore(`Wrong fix: ${aircraft.flightNumber}`, -50, 'transitions')
      }
    } else {
      const goalWp = aircraft.goal.takeoff?.waypointName
      if (goalWp === exit.waypointName) {
        this._handledSuccess += 1
        this.creditShiftWork(aircraft)
        this.awardScore(`Departure ${aircraft.flightNumber}`, 50, 'departures')
        this.maybeAwardTimeBonus(aircraft, 'departure')
        const combo = this._comboTracker.recordDeparture(this._simTimeSec)
        if (combo) this.applyComboEvent(combo)
      } else {
        this._handledFail += 1
        this.awardScore(`Wrong fix: ${aircraft.flightNumber}`, -50, 'departures')
      }
    }
  }

  private checkLevelEnd(): void {
    if (this._endState.kind !== 'inProgress') return

    // Live positioned shift: ends the moment the player hits the aircraft-worked
    // goal (the live feed never "runs out", so this is the only finish).
    if (this.isLive && isShiftComplete(this._aircraftWorked, this._shiftGoal)) {
      this._endState = { kind: 'finished', success: true }
      this.listeners.forEach((l) => l.onLevelEnd?.(this._endState))
      return
    }

    const handled = this._handledSuccess + this._handledFail
    const stillSpawning = !noMoreSpawns(this._mode)
    const stillActive = this.aircrafts.filter(
      (a) => a.status !== 'landed' && a.status !== 'completed',
    ).length
    if (stillSpawning || stillActive > 0 || handled === 0) return

    const minPct = this.difficulty.minimumSuccessPercentage
    const successRate = handled === 0 ? 0 : this._handledSuccess / handled
    // Tutorial-style single-aircraft levels (minPct = 0) pass on any
    // success; otherwise apply the threshold.
    const success = minPct > 0 ? successRate >= minPct : this._handledSuccess > 0
    this._endState = { kind: 'finished', success }
    this.listeners.forEach((l) => l.onLevelEnd?.(this._endState))
  }

  private iosOffsetToWorldPx(jsonValue: number): number {
    return (jsonValue / IOS_PX_PER_NM) * this.pxPerNM
  }

  /// Build a flight number for a freshly-spawned aircraft. Picks a
  /// country-specific operator/registration prefix from the
  /// performance table when the goal's airport has a `country`
  /// field (e.g. ES → IBE/VLG/AEA/IBS for a regional jet at LEMD);
  /// falls back to the type's generic prefix (RJ, GA, HVY, …)
  /// otherwise. Transition goals — which have no airport — fall
  /// back to the level's focus-airport country.
  /// Pick a unique 4-digit octal squawk for a new aircraft.
  /// Avoids reserved real-world codes (7500 hijack, 7600 radio
  /// failure, 7700 emergency, 7000 EU VFR, 1200 US VFR) and
  /// already-issued codes this session. Up to 50 re-rolls before
  /// accepting a duplicate — 4091 valid codes vs typical sessions
  /// of ~50 aircraft means we never hit that ceiling in practice.
  private nextSquawk(): string {
    for (let i = 0; i < 50; i++) {
      const code = this.randomOctalSquawk()
      if (!RESERVED_SQUAWKS.has(code) && !this._usedSquawks.has(code)) {
        this._usedSquawks.add(code)
        return code
      }
    }
    return this.randomOctalSquawk()
  }

  private randomOctalSquawk(): string {
    let s = ''
    for (let i = 0; i < 4; i++) s += Math.floor(this.rng() * 8)
    return s
  }

  private nextCallsign(type: AircraftType, goal?: AircraftGoal): string {
    const perf = performanceFor(type)
    const country = this.countryForGoal(goal)
    const candidates = country ? perf.countryPrefixes?.[country] : undefined
    const prefix =
      candidates && candidates.length > 0
        ? candidates[Math.floor(this.rng() * candidates.length)]
        : perf.flightNumberPrefix
    const num = 100 + Math.floor(this.rng() * 900)
    return `${prefix}${num}`
  }

  private countryForGoal(goal?: AircraftGoal): string | undefined {
    const airportName =
      goal?.land?.airportName ??
      goal?.takeoff?.airportName ??
      goal?.departure?.airportName ??
      goal?.arrival?.airportName ??
      goal?.landHelipad?.airportName
    if (airportName) {
      return this.bundle.level.airports.find((a) => a.name === airportName)?.country
    }
    // Transition goals (and goalless paths) — use the focus
    // airport's country as a country-of-airspace proxy.
    const focus = this.bundle.level.initialFocusAirportName
    const ap = focus
      ? this.bundle.level.airports.find((a) => a.name === focus)
      : this.bundle.level.airports[0]
    return ap?.country
  }

  /// Returns true when at least one aircraft is `.landing` on `runway`
  /// at `airportName` and within 5 NM of the indicated threshold end.
  /// Used by the spawn pipeline to defer takeoff/SID departures onto
  /// runways with an arrival on short final — without this check the
  /// spawner materialises an `awaitingTakeoff` aircraft directly under
  /// the inbound aircraft, and the collision detector crashes them on
  /// the next sweep. Two users reported this on KGCN; the failure mode
  /// is airport-agnostic. Mirrors the iOS fix in
  /// `FlatoutATC/Models/GameViewModel.swift::isShortFinalActive`
  /// (commit 96ccc0f on the 2.4 branch).
  ///
  /// 5 NM is the chosen short-final radius — at 130-180 kts approach
  /// speed that's 100-140 sim-seconds, comfortably more than rotation
  /// + climb-out for a regional jet. Long-final spawns (10+ NM out)
  /// still allow LUAW-style departures.
  private isShortFinalActive(
    airportName: string,
    runway: Runway,
    runwayId: string,
  ): boolean {
    const isRunway1 = runway.id1 === runwayId
    const thresholdGeo = isRunway1 ? runway.startCoordinate : runway.endCoordinate
    const threshold = this.projector.project(thresholdGeo, true)
    const shortFinalPx = 5.0 * this.pxPerNM

    for (const ac of this.aircrafts) {
      if (ac.status !== 'landing') continue
      if (!ac.landingRunway) continue
      // Match airport via the aircraft's landing-goal source. Without
      // this guard a multi-airport level (e.g. an IAD-equivalent with
      // KIAD-09 and KDCA-09) would false-positive across airports.
      const landingAirportName =
        ac.goal?.land?.airportName ??
        ac.goal?.arrival?.airportName ??
        ac.goal?.landHelipad?.airportName
      if (landingAirportName !== airportName) continue
      if (ac.landingRunway.id1 !== runway.id1) continue
      if (ac.landingRunway.id2 !== runway.id2) continue
      const dx = ac.position.x - threshold.x
      const dy = ac.position.y - threshold.y
      if (Math.hypot(dx, dy) <= shortFinalPx) return true
    }
    return false
  }

  private addAircraftFromGoal(args: {
    id: string
    flightNumber: string
    type: AircraftType
    goal: AircraftGoal
  }): boolean {
    // Takeoff and SID departure goals go through the queue rather than
    // materialising directly on the threshold. The renderer shows them
    // as small dots behind the runway, and `promoteNextAircraftInQueue`
    // turns them into real `awaitingTakeoff` aircraft once the runway
    // clears of arrivals on short final and other ground traffic.
    if (args.goal.takeoff || args.goal.departure) {
      return this.enqueueTakeoffFromGoal(args)
    }

    const result = spawnByGoal(
      {
        level: this.bundle.level,
        projector: this.projector,
        pxPerNM: this.pxPerNM,
        worldPx: this.worldPx,
        rng: this.rng,
        effectiveWindDirection: this.effectiveWindDirection,
      },
      args.type,
      args.goal,
    )
    if (!result) return false
    const aircraft = new Aircraft({
      id: args.id,
      flightNumber: args.flightNumber,
      type: args.type,
      goal: args.goal,
      position: result.position,
      headingDeg: result.headingDeg,
      altitudeFt: result.altitudeFt,
      speedKts: result.speedKts,
      status: result.status,
      directToPoint: result.initialDirectTo?.point,
      directToName: result.initialDirectTo?.name,
      departureRunway: result.departureRunway,
      squawk: this.nextSquawk(),
    })

    // STAR arrival: engage the flight plan immediately so the
    // aircraft auto-flies the procedure. The first leg's directTo
    // was set up by `spawnArrival`.
    if (args.goal.arrival) {
      const ar = args.goal.arrival
      const airport = this.bundle.level.airports.find((a) => a.name === ar.airportName)
      const star = airport?.stars?.find((s) => s.name === ar.starName)
      if (star && star.legs.length > 0) {
        aircraft.flightPlan = {
          procedureName: star.name,
          legs: star.legs,
          currentLegIndex: 0,
        }
      }
    }

    // Emergency roll — landing-goal aircraft only. Takeoff/transition
    // start on the ground or just exit; an emergency on those would
    // need a goal swap which the MVP doesn't model.
    if (
      (args.goal.land || args.goal.arrival) &&
      this.rng() < this.difficulty.emergencyChance
    ) {
      const emergency = rollEmergency({
        aircraft,
        airports: this.bundle.level.airports,
        projector: this.projector,
        pxPerNM: this.pxPerNM,
        rng: this.rng,
      })
      aircraft.emergency = emergency
      // Real-world: emergencies squawk 7700 so ATC + nearby radar
      // see them as critical. Stamp here so the data tag + flight
      // strip both reflect the change for the rest of the run.
      aircraft.squawk = '7700'
    }

    this.aircrafts.push(aircraft)
    // Spawn check-in: every new aircraft introduces itself with its
    // state + goal so the player has context the moment it appears
    // on the radar. Emergencies layer a Mayday on top.
    this.emitReadback(spawnCheckInText(aircraft))
    if (aircraft.emergency) {
      this.emitReadback(emergencyDeclareText(aircraft, aircraft.emergency.reason))
    }
    return true
  }

  /// Resolve a takeoff/SID-departure goal to its target runway, build
  /// a `PendingAircraft` slot, push it onto the per-runway queue, and
  /// try an immediate promotion. Returns false when the goal data is
  /// invalid (unknown airport or runway) — same soft-fail contract as
  /// the legacy `addAircraftFromGoal` for arrival/land/transition
  /// goals.
  private enqueueTakeoffFromGoal(args: {
    id: string
    flightNumber: string
    type: AircraftType
    goal: AircraftGoal
  }): boolean {
    const pick = pickRunwayForTakeoffGoal(
      {
        level: this.bundle.level,
        projector: this.projector,
        pxPerNM: this.pxPerNM,
        worldPx: this.worldPx,
        rng: this.rng,
        effectiveWindDirection: this.effectiveWindDirection,
      },
      args.goal,
    )
    if (!pick) return false
    const pending: PendingAircraft = {
      id: args.id,
      flightNumber: args.flightNumber,
      type: args.type,
      goal: args.goal,
      airportName: pick.airportName,
      runwayId: pick.runwayId,
      runwayHeadingDeg: pick.runwayHeadingDeg,
      threshold: pick.threshold,
      timeToFirstWaypointSec: DEFAULT_TAKEOFF_QUEUE_DEADLINE_SEC,
      isAIControlled: false,
    }
    const key = queueKeyFor(pick.airportName, pick.runwayId)
    const queue = this.takeoffQueues[key] ?? []
    queue.push(pending)
    this.takeoffQueues[key] = queue
    // Immediate promotion attempt — if the runway is clear right now,
    // the dot becomes an awaiting-takeoff aircraft on the same tick.
    this.promoteNextAircraftInQueue(key)
    return true
  }

  /// Try to promote the head of `takeoffQueues[key]` into an actual
  /// `awaitingTakeoff` aircraft. Gates:
  ///   • runway not already occupied by another `awaitingTakeoff` /
  ///     `takingOff` aircraft (multiple departures can't share a
  ///     threshold; the previous one has to rotate out first).
  ///   • no arrival on short final to this runway end (see
  ///     `isShortFinalActive` doc comment for the 5 NM rationale).
  /// Triggered from enqueue, post-rotation (takingOff → enRoute), post-
  /// touchdown (pruneOutOfBoundsAircraft removes landed aircraft), and
  /// from `RadarScene.commandGoAround` via `tryPromoteAllQueues`.
  private promoteNextAircraftInQueue(key: string): void {
    const queue = this.takeoffQueues[key]
    if (!queue || queue.length === 0) return

    const pending = queue[0]
    if (!pending) return

    // Runway-occupied check
    const occupied = this.aircrafts.some(
      (a) =>
        a.departureRunway?.airportName === pending.airportName &&
        a.departureRunway?.runwayId === pending.runwayId &&
        (a.status === 'awaitingTakeoff' || a.status === 'takingOff'),
    )
    if (occupied) return

    // Short-final check
    const airport = this.bundle.level.airports.find(
      (a) => a.name === pending.airportName,
    )
    if (!airport) return
    const runway = airport.runways.find(
      (r) => r.id1 === pending.runwayId || r.id2 === pending.runwayId,
    )
    if (!runway) return
    if (this.isShortFinalActive(pending.airportName, runway, pending.runwayId)) {
      return
    }

    // All gates passed — promote.
    queue.shift()
    this.takeoffQueues[key] = queue
    const aircraft = this.materializePendingToAircraft(pending, airport)
    this.aircrafts.push(aircraft)
    this.emitReadback(spawnCheckInText(aircraft))
  }

  /// Iterate every non-empty takeoff queue and try a promotion for
  /// each. Used after events that may have cleared a previously-
  /// blocked runway state (currently: go-around — a landing aircraft
  /// inside the short-final radius bails out). The per-runway gates
  /// inside `promoteNextAircraftInQueue` ensure we don't double-spawn.
  tryPromoteAllQueues(): void {
    for (const key of Object.keys(this.takeoffQueues)) {
      this.promoteNextAircraftInQueue(key)
    }
  }

  /// Build the live `Aircraft` for a queue slot. The runway threshold
  /// position + heading were captured at enqueue time; here we layer
  /// on the SID flight plan (for departure goals) and call-in
  /// readback. No emergency roll — emergencies on takeoff are not
  /// modeled (matches iOS, where the spawner explicitly gates
  /// emergencies on `!isTakeoff`).
  private materializePendingToAircraft(
    pending: PendingAircraft,
    airport: import('../types/LevelData').Airport,
  ): Aircraft {
    const aircraft = new Aircraft({
      id: pending.id,
      flightNumber: pending.flightNumber,
      type: pending.type,
      goal: pending.goal,
      position: { x: pending.threshold.x, y: pending.threshold.y },
      headingDeg: pending.runwayHeadingDeg,
      altitudeFt: airport.elevation ?? 0,
      speedKts: 0,
      status: 'awaitingTakeoff',
      departureRunway: {
        airportName: pending.airportName,
        runwayId: pending.runwayId,
      },
      squawk: this.nextSquawk(),
    })
    // SID flight plan — same shape as the STAR-arrival hookup in
    // addAircraftFromGoal, but for the departure-side procedure.
    const dep = pending.goal.departure
    if (dep) {
      const sid = airport.sids?.find(
        (s) => s.name === dep.sidName && s.runway === dep.runwayId,
      )
      if (sid && sid.legs.length > 0) {
        aircraft.flightPlan = {
          procedureName: sid.name,
          legs: sid.legs,
          currentLegIndex: 0,
        }
      }
    }
    return aircraft
  }

  /// Public, read-only flattened view of every queued aircraft across
  /// all runways. Renderer-friendly: the Pixi `TakeoffQueueLayer`
  /// reads this every frame and diffs against its previous dot map
  /// to add/remove/move nodes.
  takeoffQueueEntries(): readonly PendingAircraft[] {
    const flat: PendingAircraft[] = []
    for (const key of Object.keys(this.takeoffQueues)) {
      const queue = this.takeoffQueues[key]
      if (!queue) continue
      for (const p of queue) flat.push(p)
    }
    return flat
  }

  /// Spawn only the script entries that come with explicit position +
  /// heading + altitude + speed (the tutorial's RJ123). Returns the
  /// next index the timer-driven spawner should pick up from. Others
  /// are deferred to the rate-based spawner via `_mode = script`.
  private spawnPresetExplicit(): number {
    const script = this.bundle.level.scenario.presetAircraft?.script ?? []
    const cx = this.worldPx / 2
    const cy = this.worldPx / 2

    for (let i = 0; i < script.length; i++) {
      const entry = script[i]
      if (!entry) continue
      const explicit =
        entry.initialPosition &&
        entry.initialHeading != null &&
        entry.initialAltitude != null &&
        entry.initialSpeedInKnots != null
      if (!explicit) {
        // Stop at the first non-explicit entry — we walk the rest in
        // order on the timer. iOS does the same: explicit-positioned
        // entries are head-of-script "starting state", and the
        // remaining script is consumed by the timer.
        return i
      }
      const id = `preset-${i}-${entry.flightNumber ?? entry.type}`
      const flightNumber =
        entry.flightNumber ?? this.nextCallsign(entry.type, entry.goal)
      const aircraft = new Aircraft({
        id,
        flightNumber,
        type: entry.type,
        goal: entry.goal,
        position: {
          x: cx + this.iosOffsetToWorldPx(entry.initialPosition!.x),
          y: cy - this.iosOffsetToWorldPx(entry.initialPosition!.y),
        },
        headingDeg: entry.initialHeading!,
        altitudeFt: entry.initialAltitude!,
        speedKts: entry.initialSpeedInKnots!,
        squawk: this.nextSquawk(),
      })
      this.aircrafts.push(aircraft)
      this.emitReadback(spawnCheckInText(aircraft))
    }
    return script.length
  }

  private spawnInitial(): void {
    const initial =
      this.bundle.level.scenario.presetAircraft?.initialSpawn ??
      this.bundle.level.scenario.randomAircraft?.initialSpawn ??
      this.bundle.level.scenario.timed?.initialSpawn ??
      []
    let idx = 0
    for (const config of initial) {
      for (let i = 0; i < config.count; i++) {
        const id = `initial-${idx}-${config.type}`
        const flightNumber = this.nextCallsign(config.type, config.goal)
        this.addAircraftFromGoal({ id, flightNumber, type: config.type, goal: config.goal })
        idx += 1
      }
    }
  }
}

/// Active spawn mode plus the bookkeeping each mode needs to know
/// whether more aircraft are coming. Stored on the session so a
/// single `noMoreSpawns(mode)` check covers all four cases for the
/// level-end predicate.
export type SpawnMode =
  | { kind: 'script'; script: PresetAircraftData[]; nextIdx: number }
  | { kind: 'pool'; pool: PossibleGoal[]; remaining: number }
  | { kind: 'timed'; pool: PossibleGoal[]; remainingSec: number; spawned: number }
  | { kind: 'idle' }

function resolveSpawnMode(
  level: LevelData,
  nextScriptIdx: number,
  _ratePerHour: number,
): SpawnMode {
  const script = level.scenario.presetAircraft?.script ?? []
  const pool = level.possibleGoals ?? []
  // Script wins when present and not fully consumed by the explicit-
  // positioned head walk.
  if (script.length > 0 && nextScriptIdx < script.length) {
    return { kind: 'script', script, nextIdx: nextScriptIdx }
  }
  // Random scenario: count + pool. Falls back to idle when either is missing.
  const random = level.scenario.randomAircraft
  if (random && random.count > 0 && pool.length > 0) {
    return { kind: 'pool', pool, remaining: random.count }
  }
  // Timed scenario: duration + pool. Falls back to idle when either is missing.
  const timed = level.scenario.timed
  if (timed && timed.durationInSeconds > 0 && pool.length > 0) {
    return {
      kind: 'timed',
      pool,
      remainingSec: timed.durationInSeconds,
      spawned: 0,
    }
  }
  return { kind: 'idle' }
}

/// True when the mode will not spawn any more aircraft. Used by the
/// level-end predicate and the spawn tick guard.
function noMoreSpawns(mode: SpawnMode): boolean {
  switch (mode.kind) {
    case 'script':
      return mode.nextIdx >= mode.script.length
    case 'pool':
      return mode.remaining <= 0
    case 'timed':
      return mode.remainingSec <= 0
    case 'idle':
      return true
  }
}

/// Number of aircraft this mode will *eventually* contribute, beyond
/// what's already been spawned. For `timed` mode this is an estimate
/// (rate × duration / 3600) since the actual count depends on the
/// player's time-multiplier choices. Drives `_expectedTotal`.
function remainingFromMode(mode: SpawnMode, ratePerHour: number): number {
  switch (mode.kind) {
    case 'script':
      return mode.script.length - mode.nextIdx
    case 'pool':
      return mode.remaining
    case 'timed':
      return Math.max(0, Math.round((ratePerHour * mode.remainingSec) / 3600))
    case 'idle':
      return 0
  }
}

function poolOf(mode: SpawnMode): PossibleGoal[] | null {
  if (mode.kind === 'pool' || mode.kind === 'timed') return mode.pool
  return null
}

/// Walk the JSON-defined script + initialSpawn (and pool, for random
/// modes) and tally goals by type. The session uses this for the
/// LevelBrief mix readout and the in-game progress HUD.
function computeLevelGoals(
  level: LevelData,
  difficulty: DifficultySettings,
  expectedTotal: number,
  mode: SpawnMode,
): LevelGoalsSummary {
  const initialSpawn: InitialSpawnConfig[] =
    level.scenario.presetAircraft?.initialSpawn ??
    level.scenario.randomAircraft?.initialSpawn ??
    level.scenario.timed?.initialSpawn ??
    []
  let landings = 0
  let transitions = 0
  let departures = 0

  // Script-mode entries: walk the full script (including explicit-
  // positioned head — those count toward the goal mix too).
  if (mode.kind === 'script') {
    for (const e of mode.script) {
      if (e.goal.land) landings += 1
      else if (e.goal.transition) transitions += 1
      else if (e.goal.takeoff) departures += 1
    }
  }

  // initialSpawn always counts (any mode can carry it).
  for (const cfg of initialSpawn) {
    const c = cfg.count
    if (cfg.goal.land) landings += c
    else if (cfg.goal.transition) transitions += c
    else if (cfg.goal.takeoff) departures += c
  }

  // Random/timed modes: estimate the per-type breakdown from the pool
  // proportional to expectedTotal. The actual mix is determined by
  // RNG draws at runtime, but the brief just needs realistic counts.
  if (mode.kind === 'pool' || mode.kind === 'timed') {
    const totalFromMode =
      mode.kind === 'pool'
        ? mode.remaining
        : Math.max(0, expectedTotal - landings - transitions - departures)
    const breakdown = estimatePoolBreakdown(mode.pool, totalFromMode)
    landings += breakdown.landings
    transitions += breakdown.transitions
    departures += breakdown.departures
  }

  const total = landings + transitions + departures
  const totalForTarget = total > 0 ? total : expectedTotal
  const requiredPct = difficulty.minimumSuccessPercentage
  // Tutorial-style (pct=0) ⟹ pass on any single success.
  const targetCompleted = requiredPct > 0 ? Math.ceil(totalForTarget * requiredPct) : 1
  return { landings, transitions, departures, total, requiredPct, targetCompleted }
}

type DifficultyName = 'Novice' | 'Trainee' | 'Licensed' | 'Master'

function resolveDifficultyAndName(
  bundle: LevelBundle,
  name: string | undefined,
): { settings: DifficultySettings; name: DifficultyName } {
  const settings = bundle.level.difficultySettings
  const validNames: DifficultyName[] = ['Novice', 'Trainee', 'Licensed', 'Master']
  if (settings) {
    if (name && settings[name] && (validNames as string[]).includes(name)) {
      return { settings: settings[name]!, name: name as DifficultyName }
    }
    if (settings['Trainee']) return { settings: settings['Trainee'], name: 'Trainee' }
    if (settings['Novice']) return { settings: settings['Novice'], name: 'Novice' }
    const first = Object.entries(settings)[0]
    if (first) {
      const [k, v] = first
      const safeName = (validNames as string[]).includes(k)
        ? (k as DifficultyName)
        : 'Trainee'
      return { settings: v, name: safeName }
    }
  }
  return { settings: DEFAULT_DIFFICULTY, name: 'Trainee' }
}

/// Order-independent key for a pair of aircraft ids. Sorting the
/// pair means (A,B) and (B,A) both produce 'A|B', so the conflict
/// tracker's Set never double-counts.
function pairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`
}

/// Random sim-second interval until the next sharp wind event.
function randInJumpRange(rng: RandomSource): number {
  return (
    WIND_JUMP_MIN_INTERVAL_SEC +
    rng() * (WIND_JUMP_MAX_INTERVAL_SEC - WIND_JUMP_MIN_INTERVAL_SEC)
  )
}

/// Shortest signed angle delta from `a` to `b` in degrees,
/// wrapped to [-180, 180]. Used for the bias-back drift and the
/// runway-re-eval threshold check.
function shortestAngleDelta(a: number, b: number): number {
  let d = ((b - a + 540) % 360) - 180
  if (d === -180) d = 180
  return d
}

/// Wrap any degree value into [0, 360).
function wrapDeg(deg: number): number {
  let d = deg % 360
  if (d < 0) d += 360
  return d
}
